/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 31/08/2025 - 13:44:49
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 31/08/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  LocalHospital as LocalHospitalIcon,
  CalendarToday as CalendarTodayIcon,
  Medication as MedicationIcon,
  Forum as ForumIcon,
  Receipt as ReceiptIcon,
  Science as ScienceIcon,
  Assessment as AssessmentIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  School as SchoolIcon,
  Support as SupportIcon,
  CheckCircleOutline as CheckCircleIcon,
  SupportAgent as SupportAgentIcon,
  DesignServices as DesignServicesIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  East as EastIcon
} from '@mui/icons-material';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import PageTitle from '../../components/PageTitle';
import LetsTalk from '../../components/letsTalk';

// Define types for our state and data structures
interface VisibilityState {
  hero: boolean;
  overview: boolean;
  features: boolean;
  process: boolean;
  technologies: boolean;
  cta: boolean;
  testimonials: boolean;
}

interface SectionRefs {
  hero: React.RefObject<HTMLElement>;
  overview: React.RefObject<HTMLElement>;
  features: React.RefObject<HTMLElement>;
  process: React.RefObject<HTMLElement>;
  technologies: React.RefObject<HTMLElement>;
  cta: React.RefObject<HTMLElement>;
  testimonials: React.RefObject<HTMLElement>;
}

interface PortfolioProject {
  id: number;
  title: string;
  description: string;
  image: string;
  tags: string[];
}

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface ProcessStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

interface Technology {
  name: string;
  icon: React.ReactNode;
  delay: number;
}

interface Testimonial {
  id: number;
  content: string;
  author: string;
  position: string;
  image: string;
  rating: number;
}

// Custom styled components
const HeroBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#044D76', // Updated to the requested color
  color: theme.palette.common.white,
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(16, 0),
  },
}));

const HmsInterfaceBg = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.05,
  overflow: 'hidden',
}));

const GradientCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
}));

const FadeInBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const FeatureCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  backgroundColor: '#ffffff',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[10],
    '& .featureIcon': {
      transform: 'scale(1.1)',
    },
    '& .featureTitle': {
      color: '#044D76', // Updated to match button color
    },
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  transition: 'transform 0.3s ease',
  marginBottom: theme.spacing(2),
}));

const TechCard = styled(Paper)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.common.white,
  borderRadius: '12px',
  textAlign: 'center',
  transition: 'all 0.5s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 153, 255, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.12)',
    '& .techIcon': {
      transform: 'scale(1.15)',
      backgroundColor: 'rgba(4, 77, 118, 0.15)',
    }
  },
}));

const ProcessTimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  height: '100%',
  width: '2px',
  backgroundColor: 'rgba(4, 77, 118, 0.2)', // Updated to match theme
  opacity: 0.5,
}));

const ProcessStepBox = styled(Box)<{ visible: boolean; delay?: number }>(({ theme, visible, delay = 0 }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  transition: 'all 1s ease',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(10px)',
  transitionDelay: `${delay}ms`,
}));

const ProcessIconBox = styled(Box)(({ theme }) => ({
  zIndex: 10,
  flexShrink: 0,
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: '#044D76', // Updated to match button color
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  boxShadow: theme.shadows[5],
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  borderRadius: '12px',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[10],
    '& .projectImage': {
      transform: 'scale(1.1)',
    },
    '& .projectOverlay': {
      opacity: 1,
    },
  },
}));

const ProjectImage = styled('img')({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s ease',
});

const ProjectOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(to top, ${theme.palette.common.black}cc, ${theme.palette.common.black}66, transparent)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: theme.spacing(3),
  opacity: 0,
  transition: 'opacity 0.3s ease',
}));

const TechIconBox = styled(Box)(({ theme }) => ({
  height: 64,
  width: 64,
  borderRadius: '50%',
  backgroundColor: 'rgba(4, 77, 118, 0.1)', // Updated color
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px auto',
  transition: 'all 0.3s ease',
}));

// Custom component for heartbeat line animation
const HeartbeatLine = styled('path')({
  animation: 'dash 2s linear forwards',
  '@keyframes dash': {
    to: {
      strokeDashoffset: 0
    }
  }
});

const CodeEditor = styled(Box)(({ theme }) => ({
  backgroundColor: '#282c34',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-5px)',
  },
}));

const EditorHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#21252b',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const EditorDot = styled(Box)<{ color: string }>(({ color }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: 8,
}));

const EditorFilename = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.85rem',
  fontFamily: 'monospace',
  marginLeft: 8,
}));

const CodeContent = styled(Box)(({ theme }) => ({
  padding: '16px',
  overflowX: 'auto',
  maxHeight: '500px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

const PreformattedCode = styled('pre')(({ theme }) => ({
  margin: 0,
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '0.85rem',
  lineHeight: 1.5,
  color: '#e6e6e6',
}));

const TestimonialCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.common.white,
  position: 'relative',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 30,
    left: 30,
    width: 60,
    height: 60,
    borderRadius: '50% 0 50% 50%',
    backgroundColor: 'rgba(4, 77, 118, 0.1)',
    zIndex: 0
  }
}));

const TestimonialNavButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: '#044D76',
  border: '2px solid rgba(4, 77, 118, 0.2)',
  zIndex: 2,
  '&:hover': {
    backgroundColor: 'rgba(4, 77, 118, 0.1)'
  }
}));

const HmsPage: React.FC = () => {
  const theme = useTheme();
  
  const [isVisible, setIsVisible] = useState<VisibilityState>({
    hero: false,
    overview: false,
    features: false,
    process: false,
    technologies: false,
    cta: false,
    testimonials: false
  });
  
  // Initialize the refs with proper typing
  const sectionRefs: SectionRefs = {
    hero: useRef<HTMLElement>(null),
    overview: useRef<HTMLElement>(null),
    features: useRef<HTMLElement>(null),
    process: useRef<HTMLElement>(null),
    technologies: useRef<HTMLElement>(null),
    cta: useRef<HTMLElement>(null),
    testimonials: useRef<HTMLElement>(null)
  };
  
  // Track when sections come into view
  useEffect(() => {
    const observers: Record<string, IntersectionObserver> = {};
    
    Object.entries(sectionRefs).forEach(([section, ref]) => {
      observers[section] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [section]: true }));
          }
        },
        { threshold: 0.1 }
      );
      
      if (ref.current) {
        observers[section].observe(ref.current);
      }
    });
    
    return () => {
      Object.entries(observers).forEach(([section, observer]) => {
        if (sectionRefs[section as keyof SectionRefs].current) {
          observer.unobserve(sectionRefs[section as keyof SectionRefs].current!);
        }
      });
    };
  }, []);
  
  const handleContactClick = (): void => {
    console.log('Contact us clicked');
    // Add navigation logic here
  };
  
  // Sample portfolio projects
  const portfolioProjects: PortfolioProject[] = [
    {
      id: 1,
      title: 'Multi-Specialty Hospital System',
      description: 'A comprehensive hospital management system for a 500-bed multi-specialty hospital with integrated modules for all departments.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Hospital+HMS',
      tags: ['Patient Management', 'EHR', 'Billing', 'Pharmacy']
    },
    {
      id: 2,
      title: 'Clinic Management Solution',
      description: 'Specialized management system for a network of outpatient clinics with appointment scheduling and patient records management.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Clinic+System',
      tags: ['Appointment System', 'Patient Portal', 'Billing', 'Reports']
    },
    {
      id: 3,
      title: 'Laboratory Information System',
      description: 'Integrated laboratory management system with sample tracking, test management, and results reporting for a diagnostic center.',
      image: 'https://placehold.co/600x400/e4e4e7/555555?text=Lab+System',
      tags: ['Test Management', 'Results Reporting', 'Integration', 'Analytics']
    }
  ];

  // Feature items
  const featureItems: FeatureItem[] = [
    {
      icon: <LocalHospitalIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Patient Management",
      description: "Comprehensive patient registration, electronic health records (EHR), and medical history management for streamlined patient care.",
      delay: 0
    },
    {
      icon: <CalendarTodayIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Appointment Scheduling",
      description: "Efficient scheduling system for patient appointments, doctor availability management, and automated reminders.",
      delay: 100
    },
    {
      icon: <MedicationIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Pharmacy Management",
      description: "Integrated pharmacy system for medication inventory, prescription management, and automated dispensing.",
      delay: 200
    },
    {
      icon: <ForumIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Doctor-Patient Communication",
      description: "Secure messaging system between healthcare providers and patients for improved communication and care coordination.",
      delay: 300
    },
    {
      icon: <ReceiptIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Billing & Insurance",
      description: "Comprehensive billing system with insurance claim processing, patient billing, and financial reporting capabilities.",
      delay: 400
    },
    {
      icon: <ScienceIcon fontSize="large" sx={{ color: '#044D76' }} />,
      title: "Laboratory Management",
      description: "Complete lab information system with test ordering, sample tracking, result management, and integration with EHR.",
      delay: 500
    }
  ];

  // Process steps
  const processSteps: ProcessStep[] = [
    {
      icon: <AssessmentIcon />,
      title: "Assessment & Planning",
      description: "We conduct a thorough assessment of your healthcare facility's needs, workflows, and existing systems to develop a detailed implementation plan.",
      delay: 0
    },
    {
      icon: <PlaylistAddCheckIcon />,
      title: "System Configuration",
      description: "We customize and configure the HMS to match your specific requirements, including department workflows, user roles, and reporting needs.",
      delay: 200
    },
    {
      icon: <StorageIcon />,
      title: "Data Migration",
      description: "We securely migrate existing patient records, medical data, and administrative information from legacy systems to the new HMS.",
      delay: 400
    },
    {
      icon: <DesignServicesIcon />,
      title: "Integration",
      description: "We integrate the HMS with other hospital systems such as radiology, laboratory, pharmacy, and third-party services for seamless data flow.",
      delay: 600
    },
    {
      icon: <SchoolIcon />,
      title: "Training & Deployment",
      description: "We provide comprehensive training for all staff members and implement a phased deployment approach to ensure smooth transition.",
      delay: 800
    },
    {
      icon: <SupportAgentIcon />,
      title: "Support & Maintenance",
      description: "We provide ongoing technical support, regular updates, and continuous optimization to ensure your HMS continues to meet evolving needs.",
      delay: 1000
    }
  ];

  // Technologies
  const technologies: Technology[] = [
    { 
      name: "Java/Spring", 
      icon: <img src="https://spring.io/img/spring.svg" alt="Spring" width="48" height="48" />,
      delay: 0 
    },
    { 
      name: "FHIR", 
      icon: <img src="https://www.hl7.org/fhir/assets/images/fhir-logo-www.png" alt="FHIR" width="48" height="48" />,
      delay: 100 
    },
    { 
      name: "HL7", 
      icon: <img src="https://www.hl7.org/assets/img/logo.png" alt="HL7" width="48" height="48" />,
      delay: 200 
    },
    { 
      name: "DICOM", 
      icon: <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/DICOM_Logo.svg/320px-DICOM_Logo.svg.png" alt="DICOM" width="48" height="48" />,
      delay: 300 
    },
    { 
      name: "PostgreSQL", 
      icon: <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/29/Postgresql_elephant.svg/993px-Postgresql_elephant.svg.png" alt="PostgreSQL" width="48" height="48" />,
      delay: 400 
    },
    { 
      name: "React", 
      icon: <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a7/React-icon.svg/1150px-React-icon.svg.png" alt="React" width="48" height="48" />,
      delay: 500 
    },
    { 
      name: "Angular", 
      icon: <img src="https://angular.io/assets/images/logos/angular/angular.svg" alt="Angular" width="48" height="48" />,
      delay: 600 
    },
    { 
      name: "Cloud Hosting", 
      icon: <img src="https://cdn.icon-icons.com/icons2/2407/PNG/512/aws_icon_146074.png" alt="Cloud Hosting" width="48" height="48" />,
      delay: 700 
    },
    { 
      name: "Docker", 
      icon: <img src="https://www.docker.com/wp-content/uploads/2022/03/Moby-logo.png" alt="Docker" width="48" height="48" />,
      delay: 800 
    },
    { 
      name: "HIPAA Security", 
      icon: <HealthAndSafetyIcon sx={{ fontSize: 48, color: '#044D76' }} />,
      delay: 900 
    },
    { 
      name: "OAuth 2.0", 
      icon: <img src="https://oauth.net/images/oauth-logo-square.png" alt="OAuth 2.0" width="48" height="48" />,
      delay: 1000 
    },
    { 
      name: "REST API", 
      icon: <CodeIcon sx={{ fontSize: 48, color: '#044D76' }} />,
      delay: 1100 
    }
  ];

  // Testimonials
  const testimonials: Testimonial[] = [
    {
      id: 1,
      content: "The HMS implementation transformed our hospital operations. Patient wait times decreased by 35% and staff productivity improved significantly. The system's intuitive interface made adoption quick and painless.",
      author: "Dr. Sarah Johnson",
      position: "Chief Medical Officer, General Hospital",
      image: "https://randomuser.me/api/portraits/women/45.jpg",
      rating: 5
    },
    {
      id: 2,
      content: "We've been using this HMS for over a year now, and it has completely streamlined our workflow. The patient records management and appointment scheduling features are exceptional, and the support team is always responsive.",
      author: "James Wilson",
      position: "IT Director, Memorial Healthcare",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      rating: 5
    },
    {
      id: 3,
      content: "The pharmacy management module has reduced medication errors by 40% and improved inventory accuracy. Integration with our existing systems was smooth, and the training provided was comprehensive.",
      author: "Dr. Emily Chen",
      position: "Chief Pharmacist, City Medical Center",
      image: "https://randomuser.me/api/portraits/women/33.jpg",
      rating: 4
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const handlePrevTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0099ff0a' }}>
      <PageTitle title="HMS" />
      <Navbar />
      <HeroBox component="section" ref={sectionRefs.hero}>
        {/* Background elements */}
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <GradientCircle 
            sx={{ 
              top: 0, 
              right: 0, 
              width: 800, 
              height: 800, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(25%, -25%)'
            }}
          />
          <GradientCircle 
            sx={{ 
              bottom: 0, 
              left: 0, 
              width: 600, 
              height: 600, 
              background: `radial-gradient(circle, ${alpha('#0099ff', 0.2)} 0%, transparent 70%)`,
              opacity: 0.2,
              transform: 'translate(-33%, 33%)'
            }}
          />
          
          {/* HMS Interface Background */}
          <HmsInterfaceBg>
            <svg width="100%" height="100%" viewBox="0 0 1200 800">
              {/* Heartbeat Line */}
              <HeartbeatLine 
                d="M100,400 L200,400 L250,300 L300,500 L350,300 L400,500 L450,300 L500,400 L600,400" 
                stroke="white" 
                strokeWidth="2"
                strokeDasharray="1000"
                strokeDashoffset="1000"
              />
              
              {/* Medical Cross */}
              <rect x="700" y="350" width="100" height="100" fill="white" fillOpacity="0.1" />
              <rect x="725" y="325" width="50" height="150" fill="white" fillOpacity="0.1" />
              
              {/* Dashboard Elements */}
              <rect x="150" y="550" width="200" height="100" rx="10" stroke="white" strokeWidth="2" fillOpacity="0" />
              <text x="250" y="600" textAnchor="middle" fill="white" fontFamily="Arial" fontSize="14">Patient Management</text>
              
              <rect x="400" y="550" width="200" height="100" rx="10" stroke="white" strokeWidth="2" fillOpacity="0" />
              <text x="500" y="600" textAnchor="middle" fill="white" fontFamily="Arial" fontSize="14">Appointment</text>
              
              <rect x="650" y="550" width="200" height="100" rx="10" stroke="white" strokeWidth="2" fillOpacity="0" />
              <text x="750" y="600" textAnchor="middle" fill="white" fontFamily="Arial" fontSize="14">Billing</text>
              
              <rect x="900" y="550" width="200" height="100" rx="10" stroke="white" strokeWidth="2" fillOpacity="0" />
              <text x="1000" y="600" textAnchor="middle" fill="white" fontFamily="Arial" fontSize="14">Pharmacy</text>
              
              {/* Medical Icons */}
              <circle cx="250" y="200" r="40" stroke="white" strokeWidth="2" fillOpacity="0" />
              <path d="M230,200 L270,200 M250,180 L250,220" stroke="white" strokeWidth="2" />
              
              <circle cx="400" y="200" r="40" stroke="white" strokeWidth="2" fillOpacity="0" />
              <path d="M385,185 L415,215 M385,215 L415,185" stroke="white" strokeWidth="2" />
              
              <circle cx="550" y="200" r="40" stroke="white" strokeWidth="2" fillOpacity="0" />
              <path d="M535,200 L565,200 M550,185 L550,215 M535,185 L535,215 M565,185 L565,215" stroke="white" strokeWidth="2" />
            </svg>
          </HmsInterfaceBg>
        </Box>

        <Container sx={{ position: 'relative', zIndex: 10 }}>
          <Box maxWidth="md">
            <FadeInBox visible={isVisible.hero}>
              <Typography variant="h2" component="h1" gutterBottom>
                Hospital Management
                <Box component="span" display="block" sx={{ color: '#0099ff' }}>Systems</Box>
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={200}>
              <Typography variant="h5" paragraph sx={{ color: 'grey.300' }}>
                Comprehensive healthcare information systems that streamline hospital operations and enhance patient care
              </Typography>
            </FadeInBox>
            
            <FadeInBox visible={isVisible.hero} delay={400} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleContactClick}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  bgcolor: '#044D76',
                  '&:hover': { 
                    bgcolor: alpha('#044D76', 0.9),
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large" 
                href="#portfolio"
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': { 
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s',
                  borderRadius: 2,
                }}
              >
                View Our Work
              </Button>
            </FadeInBox>
          </Box>
        </Container>

        {/* Wave shape divider */}
        <Box sx={{ position: "absolute", bottom: 0, left: 0, width: "100%" }}>
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 80.5C672 71 768 35.5 864 26.625C960 17.75 1056 35.5 1152 44.375C1248 53.25 1344 53.25 1392 53.25H1440V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z"
              fill={theme.palette.background.default}
            />
          </svg>
        </Box>
      </HeroBox>

      {/* Overview Section */}
      <Box component="section" ref={sectionRefs.overview} py={10}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview}>
                <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
                  Transforming Healthcare Management
                </Typography>
                <Typography variant="body1" paragraph>
                  Our Hospital Management Systems (HMS) are designed to digitize and streamline all aspects of healthcare operations, from patient registration to discharge, billing, and beyond. We help healthcare providers deliver better patient care through efficient information management.
                </Typography>
                <Typography variant="body1" paragraph>
                  With our HMS solutions, healthcare facilities can enhance operational efficiency, improve data accuracy, ensure regulatory compliance, and ultimately provide better patient experiences and outcomes.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
                  {[
                    "Streamlined hospital operations",
                    "Enhanced patient care management",
                    "Improved financial management",
                    "HIPAA-compliant data security"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#044D76' }} />
                      <Typography variant="body1" fontWeight="medium">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </FadeInBox>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FadeInBox visible={isVisible.overview} delay={300}>
                <Box sx={{ position: 'relative' }}>
                  {/* Improved code editor styling */}
                  <CodeEditor>
                    <EditorHeader>
                      <EditorDot color="#ff5f56" />
                      <EditorDot color="#ffbd2e" />
                      <EditorDot color="#27c93f" />
                      <EditorFilename>PatientController.java</EditorFilename>
                    </EditorHeader>
                    
                    <CodeContent>
                      <PreformattedCode>
{`package com.hospital.controllers;

import com.hospital.models.Patient;
import com.hospital.services.PatientService;
import com.hospital.dto.PatientDTO;
import com.hospital.exceptions.ResourceNotFoundException;

import org.springframework.beans.factory.annotation.Autowired;
import org.springframework.http.ResponseEntity;
import org.springframework.web.bind.annotation.*;

import javax.validation.Valid;
import java.util.List;
import java.util.stream.Collectors;

/**
 * REST Controller for managing Patient records
 */
@RestController
@RequestMapping("/api/patients")
public class PatientController {

    private final PatientService patientService;
    
    @Autowired
    public PatientController(PatientService patientService) {
        this.patientService = patientService;
    }
    
    /**
     * GET /api/patients : Get all patients
     * 
     * @return the ResponseEntity with status 200 (OK) and the list of patients
     */
    @GetMapping
    public ResponseEntity<List<PatientDTO>> getAllPatients() {
        List<Patient> patients = patientService.findAll();
        List<PatientDTO> patientDTOs = patients.stream()
                .map(this::convertToDto)
                .collect(Collectors.toList());
        return ResponseEntity.ok(patientDTOs);
    }
    
    /**
     * GET /api/patients/:id : Get patient by ID
     * 
     * @param id the ID of the patient to retrieve
     * @return the ResponseEntity with status 200 (OK) and patient data
     * @throws ResourceNotFoundException if patient not found
     */
    @GetMapping("/{id}")
    public ResponseEntity<PatientDTO> getPatient(@PathVariable Long id) {
        Patient patient = patientService.findById(id);
        return ResponseEntity.ok(convertToDto(patient));
    }`}
                      </PreformattedCode>
                    </CodeContent>
                  </CodeEditor>
                  
                  {/* Background decoration */}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: -24, 
                      right: -24, 
                      width: 128, 
                      height: 128, 
                      borderRadius: '50%', 
                      bgcolor: '#0099ff',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: -32, 
                      left: -32, 
                      width: 160, 
                      height: 160, 
                      borderRadius: '50%', 
                      bgcolor: '#044D76',
                      opacity: 0.2,
                      zIndex: -1
                    }}
                  />
                </Box>
              </FadeInBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box 
        component="section" 
        ref={sectionRefs.features}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.features}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our HMS Solutions
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We offer comprehensive hospital management systems with integrated modules to manage all aspects of healthcare operations efficiently.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {featureItems.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard 
                  elevation={2}
                  visible={isVisible.features}
                  delay={feature.delay}
                >
                  <IconBox className="featureIcon">
                    {feature.icon}
                  </IconBox>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    className="featureTitle"
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Box component="section" ref={sectionRefs.process} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.process}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our HMS Implementation Process
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We follow a comprehensive approach to implementing hospital management systems that ensures smooth transition, minimal disruption, and maximum adoption.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <ProcessTimelineLine />
            
            {/* Process steps */}
            {processSteps.map((step, index) => (
              <ProcessStepBox 
                key={index}
                visible={isVisible.process}
                delay={step.delay}
                sx={{ 
                  flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                }}
              >
                <Box 
                  sx={{ 
                    width: '50%',
                    textAlign: index % 2 === 0 ? 'right' : 'left',
                    pr: index % 2 === 0 ? 6 : 0,
                    pl: index % 2 === 0 ? 0 : 6,
                  }}
                >
                  <Typography variant="h6" component="h3" gutterBottom sx={{ color: '#044D76' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
                
                <ProcessIconBox>
                  {step.icon}
                </ProcessIconBox>
                
                <Box sx={{ width: '50%' }} />
              </ProcessStepBox>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Technologies Section - Improved */}
      <Box 
        component="section" 
        ref={sectionRefs.technologies}
        py={10}
        sx={{ bgcolor: '#ffffff' }}
      >
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.technologies}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Technologies We Use
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We leverage cutting-edge healthcare technologies and standards to build secure, compliant, and efficient hospital management systems.
            </Typography>
          </FadeInBox>
          
          <Grid container spacing={4}>
            {technologies.map((tech, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <TechCard 
                  elevation={1}
                  visible={isVisible.technologies}
                  delay={tech.delay}
                >
                  <TechIconBox className="techIcon">
                    {tech.icon}
                  </TechIconBox>
                  <Typography variant="h6" fontWeight="medium" sx={{ color: '#044D76', mb: 1 }}>
                    {tech.name}
                  </Typography>
                  <Divider sx={{ width: '40%', mx: 'auto', my: 2, backgroundColor: 'rgba(4, 77, 118, 0.1)' }} />
                  <Typography variant="body2" color="text.secondary">
                    Expert implementation
                  </Typography>
                </TechCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box component="section" ref={sectionRefs.testimonials} py={10} sx={{ backgroundColor: '#0099ff0a' }}>
        <Container maxWidth="lg">
          <FadeInBox 
            visible={isVisible.testimonials}
            sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}
          >
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Client Testimonials
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Discover what healthcare organizations have to say about their experience implementing our hospital management systems.
            </Typography>
          </FadeInBox>
          
          <Box sx={{ position: 'relative', maxWidth: 'md', mx: 'auto' }}>
            {/* Left Navigation */}
            <TestimonialNavButton 
              onClick={handlePrevTestimonial}
              sx={{ 
                left: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
            
            {/* Testimonial Card */}
            <TestimonialCard elevation={3}>
              <Typography 
                variant="body1" 
                component="blockquote" 
                sx={{ 
                  fontStyle: 'italic', 
                  position: 'relative',
                  mb: 4,
                  '&::before': {
                    content: '"\\201C"',
                    position: 'absolute',
                    left: -30,
                    top: -20,
                    fontSize: '5rem',
                    color: 'rgba(4, 77, 118, 0.1)',
                    fontFamily: 'Georgia, serif'
                  }
                }}
              >
                "{testimonials[currentTestimonial].content}"
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {[...Array(5)].map((_, index) => (
                  <Box 
                    key={index}
                    component="span"
                    sx={{ 
                      color: index < testimonials[currentTestimonial].rating ? '#0099ff' : 'rgba(0, 0, 0, 0.2)',
                      mx: 0.5,
                      fontSize: '24px',
                    }}
                  >
                    ★
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box 
                  component="img"
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].author}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    mr: 2,
                    border: '2px solid rgba(4, 77, 118, 0.2)'
                  }}
                />
                <Box>
                  <Typography variant="h6" sx={{ color: '#044D76', fontWeight: 'bold' }}>
                    {testimonials[currentTestimonial].author}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testimonials[currentTestimonial].position}
                  </Typography>
                </Box>
              </Box>
            </TestimonialCard>
            
            {/* Right Navigation */}
            <TestimonialNavButton 
              onClick={handleNextTestimonial}
              sx={{ 
                right: { xs: 10, md: -70 },
                top: '50%', 
                transform: 'translateY(-50%)',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TestimonialNavButton>
          </Box>
        </Container>
      </Box>

      {/* Portfolio Section */}
      <Box component="section" id="portfolio" py={10}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 'md', mx: 'auto', mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ color: '#044D76' }}>
              Our HMS Portfolio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore some of our successful hospital management system implementations and the results we've delivered for healthcare providers.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {portfolioProjects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard elevation={4}>
                  <Box sx={{ height: 240, position: 'relative' }}>
                    <ProjectImage 
                      src={project.image} 
                      alt={project.title}
                      className="projectImage"
                    />
                    <ProjectOverlay className="projectOverlay">
                      <Typography variant="h6" component="h3" gutterBottom sx={{ color: 'common.white' }}>
                        {project.title}
                      </Typography>
                      <Typography variant="body2" paragraph sx={{ color: 'grey.200' }}>
                        {project.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.tags.map((tag, i) => (
                          <Chip 
                            key={i}
                            label={tag}
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(4, 77, 118, 0.8)', 
                              color: 'white',
                              fontWeight: 'medium',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>
                    </ProjectOverlay>
                  </Box>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button 
              variant="contained" 
              size="large"
              sx={{ 
                px: 4, 
                py: 1.5, 
                bgcolor: '#044D76',
                color: 'common.white',
                '&:hover': {
                  bgcolor: alpha('#044D76', 0.9),
                  transform: 'translateY(-4px)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 2,
              }}
            >
              View All Projects
            </Button>
          </Box>
        </Container>
      </Box>
      <LetsTalk/>
      <Footer />
    </Box>
  );
};

export default HmsPage;